import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { tickToPrice } from '@uniswap/v3-sdk'
import * as _ from 'lodash-es'
import { usePostHog } from 'posthog-js/react'
import { type Hex, isAddressEqual } from 'viem'
import {
  useAccount,
  usePublicClient,
  useSwitchChain,
  useWriteContract,
} from 'wagmi'

import { active_chain } from '@repo/common/blockchain/config'

import { LimitOrdersManagerAbi } from '../blockchain/abis/LimitOrdersManager'
import { useCreateAndSignPermit2 } from '../blockchain/useCreateAndSignPermit2'
import { useGetPoolConfig } from '../fission_dex'
import { useActiveFundInfo } from '../funds'
import { amount_to_tokens, tokens_to_amount } from '../helpers/block_helpers'
import { token_balance_change } from '../helpers/invalidator_helpers'
import { convert_to_u_token } from '../helpers/tick_helpers'
import { disableKeyWhenMissing } from '../helpers/utilities'
import {
  useGetAddressToPricedSymbol,
  useGetSymbolToDecimal,
} from '../portfolio'
import { type FundSymbol, useGetTokenAddress } from '../products'

import { keys } from './limit_orders_queries'

export type LimitOrderType = {
  id: bigint
  fund_symbol: FundSymbol
  sell_symbol: FundSymbol | 'USDC'
  tokens: number
  price: number
  tick: number
}
// eslint-disable-next-line complexity, max-lines-per-function
export function useGetLimitOrders({
  fund_symbol,
}: {
  fund_symbol: FundSymbol
}) {
  const { address: wallet_address } = useAccount()
  const client = usePublicClient({ chainId: active_chain.id })
  const usdc_address = useGetTokenAddress({ symbol: 'USDC' })
  const symbol_to_decimal = useGetSymbolToDecimal()
  const { data: fund } = useActiveFundInfo({ symbol: fund_symbol })
  const fund_address = fund?.token_address
  const limit_order_address = fund?.limit_order_address

  const enabled =
    client != null &&
    wallet_address != null &&
    fund_address != null &&
    usdc_address != null &&
    symbol_to_decimal.isSuccess &&
    limit_order_address != null

  return useQuery<LimitOrderType[]>({
    queryKey: keys.limit_orders.all({
      fund_symbol,
      wallet_address: disableKeyWhenMissing(wallet_address),
    }).queryKey,
    queryFn: !enabled
      ? skipToken
      : async () => {
          const order_ids = await client.readContract({
            abi: LimitOrdersManagerAbi,
            functionName: 'tokensOf',
            address: limit_order_address,
            args: [wallet_address],
          })

          const raw_orders = (
            await client.multicall({
              contracts: order_ids.map((order_id) => ({
                abi: LimitOrdersManagerAbi,
                functionName: 'getOrder' as const,
                address: limit_order_address,
                args: [order_id],
              })),
            })
          ).map((p, index) => ({ ...p, id: order_ids[index] }))

          type raw_order_passed_type = Exclude<
            (typeof raw_orders)[number],
            { status: 'failure' }
          >

          const fund_token = convert_to_u_token({
            symbol: fund_symbol,
            address: fund_address,
            decimals: symbol_to_decimal[fund_symbol],
          })

          const usdc_token = convert_to_u_token({
            symbol: 'USDC',
            address: usdc_address,
            decimals: symbol_to_decimal['USDC'],
          })

          return raw_orders
            .filter(
              (order): order is raw_order_passed_type =>
                order.status != 'failure',
            )
            .map((order) => {
              const sell_symbol = isAddressEqual(
                order.result.tokenToSell,
                fund_address,
              )
                ? fund_symbol
                : 'USDC'
              return {
                id: order.id,
                sell_symbol,
                fund_symbol,
                tokens: amount_to_tokens(
                  order.result.amount,
                  symbol_to_decimal[sell_symbol],
                ),
                price: Number(
                  tickToPrice(
                    fund_token,
                    usdc_token,
                    order.result.tick,
                  ).toFixed(2),
                ),
                tick: order.result.tick,
              }
            })
        },
  })
}

// eslint-disable-next-line complexity, max-lines-per-function
export function useCreateLimitOrder({
  fund_symbol,
}: {
  fund_symbol: FundSymbol
}) {
  const queryClient = useQueryClient()
  const symbol_to_decimal = useGetSymbolToDecimal()
  const { writeContractAsync } = useWriteContract()
  const { switchChainAsync } = useSwitchChain()
  const { address: wallet_address } = useAccount()
  const { data: fund } = useActiveFundInfo({ symbol: fund_symbol })
  const client = usePublicClient({ chainId: active_chain.id })
  const posthog = usePostHog()
  const { createSingleAsync, isReady: permit_is_ready } =
    useCreateAndSignPermit2()
  const usdc_address = useGetTokenAddress({ symbol: 'USDC' })
  const { data: pool } = useGetPoolConfig(fund_symbol)
  const limit_order_address = fund?.limit_order_address

  const fund_address = fund?.token_address

  const isReady =
    symbol_to_decimal.isSuccess &&
    permit_is_ready &&
    usdc_address != null &&
    fund_address != null &&
    limit_order_address != null &&
    fund != null &&
    client != null &&
    pool != null &&
    wallet_address != null

  const mutation = useMutation({
    mutationFn: async ({
      price_tick,
      price,
      sell_tokens,
      symbol_in,
    }: {
      price: number
      price_tick: number
      sell_tokens: number
      symbol_in: FundSymbol | 'USDC'
    }) => {
      if (!isReady) {
        console.warn('limit order called before ready')
        return
      }
      const symbol_in_address =
        symbol_in == 'USDC' ? usdc_address : fund_address

      const sell_amount = tokens_to_amount(
        sell_tokens,
        symbol_to_decimal[symbol_in],
      )

      // ensure network is correct
      await switchChainAsync({ chainId: active_chain.id })

      const { permitSingle, signature } = await createSingleAsync({
        token_address: symbol_in_address,
        token_symbol: symbol_in,
        destination_address: limit_order_address,
        token_amount: sell_amount,
      })

      const tx = await writeContractAsync({
        abi: LimitOrdersManagerAbi,
        address: limit_order_address,
        functionName: 'createWithPermit2',
        args: [price_tick, sell_amount, permitSingle, signature],
        chainId: active_chain.id,
      })

      await client.waitForTransactionReceipt({ hash: tx })
      posthog.capture('limit order', {
        type: 'create',
        fund: fund.symbol,
        symbol_in,
        price,
        sell_tokens,
      })
    },
    onSuccess: () => {
      void token_balance_change({
        queryClient,
        token_address: fund!.token_address,
        wallet_address: wallet_address!,
      })

      void queryClient.invalidateQueries({
        queryKey: keys.limit_orders.all({
          fund_symbol,
          wallet_address: wallet_address!,
        }).queryKey,
      })
    },
  })

  return {
    isReady,
    ...mutation,
  }
}

// eslint-disable-next-line complexity, max-lines-per-function
export function useAddToLimitOrder({
  fund_symbol,
}: {
  fund_symbol: FundSymbol
}) {
  const queryClient = useQueryClient()
  const symbol_to_decimal = useGetSymbolToDecimal()
  const { writeContractAsync } = useWriteContract()
  const { switchChainAsync } = useSwitchChain()
  const { address: wallet_address } = useAccount()
  const { data: fund } = useActiveFundInfo({ symbol: fund_symbol })
  const client = usePublicClient({ chainId: active_chain.id })
  const posthog = usePostHog()
  const { createSingleAsync, isReady: permit_is_ready } =
    useCreateAndSignPermit2()
  const usdc_address = useGetTokenAddress({ symbol: 'USDC' })
  const { data: pool } = useGetPoolConfig(fund_symbol)
  const limit_order_address = fund?.limit_order_address

  const fund_address = fund?.token_address

  const isReady =
    symbol_to_decimal.isSuccess &&
    permit_is_ready &&
    usdc_address != null &&
    fund_address != null &&
    fund != null &&
    client != null &&
    pool != null &&
    limit_order_address != null &&
    wallet_address != null

  const mutation = useMutation({
    mutationFn: async ({
      order_token_id,
      price,
      sell_tokens,
      symbol_in,
    }: {
      order_token_id: bigint
      price: number
      sell_tokens: number
      symbol_in: FundSymbol | 'USDC'
    }) => {
      if (!isReady) {
        console.warn('limit order called before ready')
        return
      }
      const symbol_in_address =
        symbol_in == 'USDC' ? usdc_address : fund_address

      const sell_amount = tokens_to_amount(
        sell_tokens,
        symbol_to_decimal[symbol_in],
      )

      // ensure network is correct
      await switchChainAsync({ chainId: active_chain.id })

      const { permitSingle, signature } = await createSingleAsync({
        token_address: symbol_in_address,
        token_symbol: symbol_in,
        destination_address: limit_order_address,
        token_amount: sell_amount,
      })

      const tx = await writeContractAsync({
        abi: LimitOrdersManagerAbi,
        address: limit_order_address,
        functionName: 'addWithPermit2',
        args: [order_token_id, sell_amount, permitSingle, signature],
        chainId: active_chain.id,
      })

      await client.waitForTransactionReceipt({ hash: tx })
      posthog.capture('limit order', {
        type: 'add',
        fund: fund.symbol,
        symbol_in,
        price,
        sell_tokens,
      })
    },
    onSuccess: () => {
      void token_balance_change({
        queryClient,
        token_address: fund!.token_address,
        wallet_address: wallet_address!,
      })

      void queryClient.invalidateQueries({
        queryKey: keys.limit_orders.all({
          fund_symbol,
          wallet_address: wallet_address!,
        }).queryKey,
      })
    },
  })

  return {
    isReady,
    ...mutation,
  }
}

// eslint-disable-next-line complexity, max-lines-per-function
export function useEarlyLimitOrderExit({
  fund_symbol,
}: {
  fund_symbol: FundSymbol
}) {
  const queryClient = useQueryClient()
  const { writeContractAsync } = useWriteContract()
  const { switchChainAsync } = useSwitchChain()
  const { address: wallet_address } = useAccount()
  const client = usePublicClient({ chainId: active_chain.id })
  const posthog = usePostHog()
  const { data: fund } = useActiveFundInfo({ symbol: fund_symbol })
  const limit_order_address = fund?.limit_order_address

  const isReady =
    client != null && limit_order_address != null && wallet_address != null

  const mutation = useMutation({
    mutationFn: async ({ order_token_id }: { order_token_id: bigint }) => {
      if (!isReady) {
        console.warn('limit order called before ready')
        return
      }

      // ensure network is correct
      await switchChainAsync({ chainId: active_chain.id })

      const tx = await writeContractAsync({
        abi: LimitOrdersManagerAbi,
        address: limit_order_address,
        functionName: 'earlyExit',
        args: [order_token_id],
        chainId: active_chain.id,
      })

      await client.waitForTransactionReceipt({ hash: tx })
      posthog.capture('limit order', {
        type: 'early exit',
        fund: fund_symbol,
      })
    },
    onSuccess: () => {
      void token_balance_change({
        queryClient,
        token_address: fund!.token_address,
        wallet_address: wallet_address!,
      })
      void queryClient.invalidateQueries({
        queryKey: keys.limit_orders.all({
          fund_symbol,
          wallet_address: wallet_address!,
        }).queryKey,
      })
    },
  })

  return {
    isReady,
    ...mutation,
  }
}

export function useGetLimitOrderPaused({
  fund_symbol,
}: {
  fund_symbol: FundSymbol
}) {
  const { data: fund } = useActiveFundInfo({ symbol: fund_symbol })
  const limit_order_address = fund?.limit_order_address
  const client = usePublicClient({ chainId: active_chain.id })

  const enabled = limit_order_address != null && client != null

  return useQuery<boolean>({
    queryKey: keys.limit_orders.details({ fund_symbol })._ctx.paused.queryKey,
    queryFn: !enabled
      ? skipToken
      : async () => {
          return await client.readContract({
            abi: LimitOrdersManagerAbi,
            address: limit_order_address,
            functionName: 'paused',
          })
        },
  })
}

export type LimitOrderLimits = {
  max_orders_count_per_user: bigint
  max_orders_count_per_tick: bigint
  fund: {
    max_tokens_per_order: number
    max_tokens_per_tick: number
  }
  usdc: {
    max_tokens_per_order: number
    max_tokens_per_tick: number
  }
}

export function useGetLimits({ fund_symbol }: { fund_symbol: FundSymbol }) {
  const { data: fund } = useActiveFundInfo({ symbol: fund_symbol })
  const limit_order_address = fund?.limit_order_address
  const client = usePublicClient({ chainId: active_chain.id })
  const symbol_to_decimal = useGetSymbolToDecimal()

  const enabled =
    limit_order_address != null && client != null && symbol_to_decimal.isSuccess

  return useQuery<LimitOrderLimits>({
    queryKey: keys.limit_orders.details({ fund_symbol })._ctx.limits.queryKey,
    queryFn: !enabled
      ? skipToken
      : // eslint-disable-next-line complexity
        async () => {
          const raw_data = await client.multicall({
            contracts: [
              {
                abi: LimitOrdersManagerAbi,
                address: limit_order_address,
                functionName: 'maxOrdersCountPerUser',
              },
              {
                abi: LimitOrdersManagerAbi,
                address: limit_order_address,
                functionName: 'maxOrdersCountPerTick',
              },
              {
                abi: LimitOrdersManagerAbi,
                address: limit_order_address,
                functionName: 'fundOrderLimits',
              },
              {
                abi: LimitOrdersManagerAbi,
                address: limit_order_address,
                functionName: 'usdcOrderLimits',
              },
            ],
          })

          return {
            max_orders_count_per_user: raw_data[0].result || 0n,
            max_orders_count_per_tick: raw_data[1].result || 0n,
            fund: {
              max_tokens_per_order: amount_to_tokens(
                raw_data[2].result?.[0] || 0n,
                symbol_to_decimal[fund_symbol],
              ),
              max_tokens_per_tick: amount_to_tokens(
                raw_data[2].result?.[1] || 0n,
                symbol_to_decimal[fund_symbol],
              ),
            },
            usdc: {
              max_tokens_per_order: amount_to_tokens(
                raw_data[3].result?.[0] || 0n,
                symbol_to_decimal[fund_symbol],
              ),
              max_tokens_per_tick: amount_to_tokens(
                raw_data[3].result?.[1] || 0n,
                symbol_to_decimal['USDC'],
              ),
            },
          }
        },
  })
}

export type LimitOrderTickUsage = {
  count: bigint
  tokens: number
  sell_symbol: FundSymbol | 'USDC'
}

export function useGetTickUsage({
  fund_symbol,
  tick,
  price,
}: {
  fund_symbol: FundSymbol
  tick: number
  price: number
}) {
  const { data: fund } = useActiveFundInfo({ symbol: fund_symbol })
  const limit_order_address = fund?.limit_order_address
  const client = usePublicClient({ chainId: active_chain.id })
  const symbol_to_decimal = useGetSymbolToDecimal()
  const address_to_priced_symbol = useGetAddressToPricedSymbol()

  const enabled =
    limit_order_address != null &&
    client != null &&
    symbol_to_decimal.isSuccess &&
    address_to_priced_symbol.isSuccess

  return useQuery<LimitOrderTickUsage>({
    queryKey: keys.limit_orders
      .details({ fund_symbol })
      ._ctx.tick_usage({ tick, price }).queryKey,
    queryFn: !enabled
      ? skipToken
      : // eslint-disable-next-line complexity
        async () => {
          const raw_data = await client.multicall({
            contracts: [
              {
                abi: LimitOrdersManagerAbi,
                address: limit_order_address,
                functionName: 'getTickOrderCount',
                args: [tick],
              },
              {
                abi: LimitOrdersManagerAbi,
                address: limit_order_address,
                functionName: 'getTickOrderAmount',
                args: [tick],
              },
            ],
          })

          const sell_symbol = address_to_priced_symbol[
            raw_data[1].result![1].toLowerCase() as Hex
          ] as LimitOrderTickUsage['sell_symbol']

          return {
            count: raw_data[0].result || 0n,
            tokens: amount_to_tokens(
              raw_data[1].result?.[0] || 0n,
              symbol_to_decimal[sell_symbol],
            ),
            sell_symbol,
          }
        },
  })
}
